<template v-cloak>
  <Head :title="formData.title"/>

  <main>
    <div class="container-fluid mobile-fix-container">
      <div id="landing-page-form" class="mt-4 mb-4 mobile-fix-rounded">

        <div v-show="isInitialLoading" class="loading-section">
          <div class="text-center">
            <h5 class="mb-3">Loading donation form...</h5>
            <div class="loading-spinner"></div>
            <p class="text-muted mt-3 mb-0">Please wait while we prepare your form</p>
          </div>
        </div>

        <div class="alert alert-danger" v-if="Object.keys($attrs.errors).length && !isProcessing && !isInitialLoading">
          <ul class="mb-0">
            <li v-for="error in $attrs.errors" :key="error">{{ error }}</li>
          </ul>
        </div>

        <template v-if="!isProcessing && !isInitialLoading">
          <h4 class="mb-3">
            <span class="badge badge-danger step">{{ $t('donation-form.step') }} 1</span>
            {{ $t('donation-form.donation_details') }}
          </h4>

          <PackageSection
              v-if="formData.mandate_application_type !== '03'"
              :form-data="formData"
              :donations="donations"
              :summary="summary"
              :campaigns="campaigns"
              :total="total"
              :errors="formErrors"
              :payment-gateway-fee="paymentGatewayFee"
              @plus="updatePackage"
              @minus="updatePackage"
          />

          <PackageSectionDisplayOnly
              v-else
              :form-data="formData"
              :donations="donations"
              :summary="summary"
              :campaigns="campaigns"
              :total="total"
              @plus="updatePackage"
              @minus="updatePackage"
          />

          <hr>

          <h4 class="mb-3">
            <span class="badge badge-danger step">{{ $t('donation-form.step') }} 2</span>
            {{ $t('donation-form.donor_details') }}
          </h4>
        </template>

        <PaymentSection
            v-show="!isInitialLoading"
            :form-data="formData"
            :total="total"
            :summary="summary"
            :city-opts="cityOpts"
            :state-opts="stateOpts"
            :country-opts="countryOpts"
            :bank-accounts="bankAccounts"
            :payment-gateways="paymentGateways"
            :tenant="tenant"
            :demo_mode="formData.demo_mode"
            :fpx-banks-list="fpxBanksList"
            :fpx-banks-logo="fpxBanksLogo"
            @processing="isProcessing = true"
            @done-processing="handleProcessingComplete"
            @payment-method-selected="(fee) => paymentGatewayFee = fee"
        />
      </div>
    </div>
  </main>
</template>

<script setup>
import { computed, onBeforeMount, onMounted, onBeforeUnmount, ref, nextTick, watch } from "vue";
import { Head } from "@inertiajs/vue3";
import PackageSection from "@/Pages/Donations/ClassicForm/PackageSection";
import PaymentSection from "@/Pages/Donations/ClassicForm/PaymentSection";
import PackageSectionDisplayOnly from "@/Pages/Donations/ClassicForm/PackageSectionDisplayOnly";
import useHelpers from "@/Composables/UseHelpers";
import useTrackingCode from "@/Composables/UseTrackingCode";

const { sendFacebookPixelAddToCartEvent, sendGoogleAdsAddToCartEvent } = useTrackingCode();
const { $t } = useHelpers();

const props = defineProps([
  'tenant', 'formData', 'bankAccounts', 'campaigns',
  'cityOpts', 'stateOpts', 'countryOpts', 'paymentGateways',
  'fpxBanksList', 'fpxBanksLogo'
]);

Lang.setLocale(props.formData.language ?? 'en');

const donations = ref([]);
const summary = ref({});
const isProcessing = ref(false);
const isInitialLoading = ref(true);
const formErrors = ref({});
const paymentGatewayFee = ref('');

const total = computed(() =>
    donations.value.reduce((sum, donation) => sum + (donation.amount * donation.count), 0)
);

let heightTimeout;
let lastSentHeight = 0;
let isReady = false;
let observer = null;

const getFormHeight = () => {
  const container = document.getElementById('landing-page-form');
  if (!container) return 400;

  const rect = container.getBoundingClientRect();
  const containerHeight = container.scrollHeight;
  const bodyHeight = Math.max(document.body.scrollHeight, document.body.offsetHeight);

  return Math.max(containerHeight + 50, bodyHeight, rect.height + rect.top + 50);
};

const sendHeight = () => {
  if (!isReady) return;

  clearTimeout(heightTimeout);
  heightTimeout = setTimeout(() => {
    const newHeight = getFormHeight();

    if (Math.abs(newHeight - lastSentHeight) > 5) {
      lastSentHeight = newHeight;

      try {
        window.parent.postMessage({
          type: 'idse-form-height',
          height: newHeight,
          isProcessing: isProcessing.value || isInitialLoading.value
        }, '*');
      } catch (e) {}
    }
  }, 100);
};

const setupObserver = () => {
  if (observer) observer.disconnect();

  observer = new MutationObserver(() => {
    sendHeight();
  });

  const container = document.getElementById('landing-page-form');
  if (container) {
    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
      characterData: true
    });
  }

  if (document.body) {
    observer.observe(document.body, {
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });
  }
};

watch([isProcessing, isInitialLoading], () => {
  nextTick(() => sendHeight());
}, { immediate: true });

watch([donations, formErrors], () => {
  nextTick(() => sendHeight());
}, { deep: true });

onBeforeMount(() => {
  props.campaigns.forEach(campaign => {
    donations.value.push(...campaign.packages);
  });

  summary.value = donations.value.reduce((acc, donation) => {
    acc[donation.campaign] = acc[donation.campaign] || [];
    acc[donation.campaign].push(donation);
    return acc;
  }, {});
});

onMounted(() => {
  if (!document.querySelector('[data-embedded-styles]')) {
    const style = document.createElement('style');
    style.setAttribute('data-embedded-styles', 'true');
    style.textContent = `
      body, html { margin: 0 !important; padding: 0 !important; overflow-x: hidden !important; }
      body#form-body { background: transparent !important; }
      .phpdebugbar, [id*="debug"] { display: none !important; }
      html::-webkit-scrollbar, body::-webkit-scrollbar { display: none !important; }
      html { scrollbar-width: none !important; -ms-overflow-style: none !important; }

      .loading-section {
        min-height: 200px; padding: 2rem 1rem; display: flex; flex-direction: column;
        justify-content: center; align-items: center; background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        border-radius: 8px; margin: 1rem 0;
      }
      .loading-section h5 { margin-bottom: 1rem; color: #495057; font-weight: 600; }
      .loading-spinner {
        width: 40px; height: 40px; border: 3px solid #e9ecef; border-top: 3px solid #007bff;
        border-radius: 50%; animation: spin 1s linear infinite; margin-top: 1rem;
      }
      @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      @media (max-width: 768px) { .loading-section { min-height: 150px; padding: 1.5rem 1rem; } }
    `;
    document.head.appendChild(style);
  }

  if (!window.idseHeightListenerAdded) {
    window.addEventListener('message', (event) => {
      if (event.data.type === 'idse-request-height' && isReady) {
        sendHeight();
      }
    });
    window.idseHeightListenerAdded = true;
  }

  window.addEventListener('resize', sendHeight);

  setTimeout(() => {
    isReady = true;
    setupObserver();
    isInitialLoading.value = false;

    nextTick(() => {
      try {
        window.parent.postMessage({ type: 'idse-ready' }, '*');
      } catch (e) {}
      sendHeight();
    });
  }, 50);
});

onBeforeUnmount(() => {
  if (observer) observer.disconnect();
  if (heightTimeout) clearTimeout(heightTimeout);
  window.removeEventListener('resize', sendHeight);
});

const handleProcessingComplete = (result) => {
  isProcessing.value = false;
  formErrors.value = result.errors || {};

  if (result.hasErrors) {
    nextTick(() => {
      document.getElementById('landing-page-form')?.scrollIntoView({ behavior: 'smooth' });
    });
  }

  sendHeight();
};

const updatePackage = (packageItem, isIncrement = true) => {
  if (isIncrement) {
    if (packageItem.count >= 99) return;
    packageItem.count++;

    if (props.formData.facebook_tracking?.facebook_pixel_id) {
      sendFacebookPixelAddToCartEvent(packageItem, props.formData, props.formData.module);
    }

    if (props.formData.google_ads_tracking?.google_ads_id &&
        props.formData.google_ads_tracking.add_to_cart_conversion_label) {
      sendGoogleAdsAddToCartEvent(packageItem, props.formData, props.formData.module);
    }
  } else {
    if (packageItem.count === 0) return;
    packageItem.count--;
  }

  nextTick(() => sendHeight());
};
</script>

<style>
[v-cloak] { display: none; }
</style>